/*
 * Copyright (c) 2019 Pegasystems Inc.
 * All rights reserved.
 */
import { publicConstants } from '../constants';
import type { CaseAction, DxAPIResponseObject } from '../case/types';

import ObjectTransformer from './object-transformer';

const setCaseLevelMessages = (confirmationNote: any, translatedDXAPIResponse: DxAPIResponseObject) => {
  const caseMessages = [confirmationNote];
  translatedDXAPIResponse.data = translatedDXAPIResponse.data ?? {};
  translatedDXAPIResponse.data.caseMessages = caseMessages;
  return translatedDXAPIResponse;
};

const getInfo = (options?: any): string => {
  if (ObjectTransformer.getNeedsMapping() || options?.info === publicConstants.OBJECT_INFO.OBJECT_INFO) {
    return publicConstants.OBJECT_INFO.OBJECT_INFO;
  }
  return publicConstants.CASE_INFO.CASE_INFO;
};

export const updateAvailableActionsInResponse = (
  dxAPIResponse: DxAPIResponseObject,
  options?: { stopMergeActions: boolean }
) => {
  const info = getInfo(options);
  dxAPIResponse.data = dxAPIResponse.data ?? {};
  /* istanbul ignore next */
  const { assignments, availableActions = [] } = dxAPIResponse.data[info as keyof typeof dxAPIResponse.data] ?? {};
  /* Use STOPMERGEACTIONS to restrict localactions for Menubutton in Review view */
  if (
    assignments &&
    Array.isArray(assignments) &&
    assignments.length > 0 &&
    assignments[0].actions &&
    Array.isArray(assignments[0].actions) &&
    !options?.stopMergeActions
  ) {
    const currentAssignmentActions: [CaseAction] = assignments[0].actions;
    currentAssignmentActions.forEach((action) => {
      if (action.type === 'Assignment' || action.type === 'LocalAction') {
        availableActions.push(action);
      }
    });
    /* istanbul ignore next */
    dxAPIResponse.data[info as keyof typeof dxAPIResponse.data] =
      dxAPIResponse.data[info as keyof typeof dxAPIResponse.data] ?? {};
    dxAPIResponse.data[info as keyof typeof dxAPIResponse.data].availableActions = availableActions;
  }
};

/**
 * this function helps to translate the dxAPIResponse
 * @function getTranslatedDXAPIResponse
 * @param options contains flag which decide the treatment on response
 * @returns - translatedDXAPIResponse
 */
// eslint-disable-next-line sonarjs/cognitive-complexity
export const getTranslatedDXAPIResponse = (dxAPIResponse: DxAPIResponseObject, options?: any) => {
  const info = getInfo(options);
  if (dxAPIResponse.confirmationLinks) {
    dxAPIResponse.data = dxAPIResponse.data ?? {};
    dxAPIResponse.data[info as keyof typeof dxAPIResponse.data] =
      dxAPIResponse.data[info as keyof typeof dxAPIResponse.data] ?? {};
    dxAPIResponse.data[info as keyof typeof dxAPIResponse.data].confirmationLinks = dxAPIResponse.confirmationLinks;
  }

  if (!dxAPIResponse.uiResources && dxAPIResponse.confirmationNote) {
    let confirmationNoteResponse = dxAPIResponse;
    const { confirmationNote } = confirmationNoteResponse;
    updateAvailableActionsInResponse(confirmationNoteResponse);
    confirmationNoteResponse = setCaseLevelMessages(confirmationNote, confirmationNoteResponse);
    return confirmationNoteResponse;
  }

  if (!dxAPIResponse.uiResources || !dxAPIResponse.data || !dxAPIResponse?.uiResources?.root) return dxAPIResponse;

  const translatedDXAPIResponse = dxAPIResponse;

  const {
    uiResources: { actionButtons, navigation },
    nextAssignmentInfo
  } = translatedDXAPIResponse;

  if (translatedDXAPIResponse.data && translatedDXAPIResponse.data[info as keyof typeof dxAPIResponse.data]) {
    if (actionButtons) {
      translatedDXAPIResponse.data[info as keyof typeof dxAPIResponse.data].actionButtons = actionButtons;
    }
    if (navigation) {
      translatedDXAPIResponse.data[info as keyof typeof dxAPIResponse.data].navigation = navigation;
    }
    /* In loadview epic remove the availableActions node to fix mergeDeep issue */
    if (options && options[publicConstants.ACTIONS.DELETEAVIALACTIONS]) {
      delete translatedDXAPIResponse.data[info as keyof typeof dxAPIResponse.data].availableActions;
    }
    /* update available actions */
    updateAvailableActionsInResponse(translatedDXAPIResponse, options);
    /* In case of ConfirmView both confirmationNote and Ui resources will present in DX API response */
    if (dxAPIResponse.confirmationNote) {
      const { confirmationNote } = translatedDXAPIResponse;
      const caseMessages = [confirmationNote];
      translatedDXAPIResponse.data.caseMessages = caseMessages;
    }
  }

  if (options && options.isCreateDataObject) {
    translatedDXAPIResponse.data = translatedDXAPIResponse.data ?? {};
    translatedDXAPIResponse.data.dataInfo = {};
    translatedDXAPIResponse.data.dataInfo.content = translatedDXAPIResponse.data.pyPortal;
    translatedDXAPIResponse.uiResources.root.config.context = 'dataInfo.content';

    translatedDXAPIResponse.uiResources.context_data.dataInfo =
      translatedDXAPIResponse.uiResources.context_data.dataInfo ?? {};
    translatedDXAPIResponse.uiResources.context_data.dataInfo.content =
      translatedDXAPIResponse.uiResources.context_data.dataInfo.content ?? {};
    translatedDXAPIResponse.uiResources.context_data.dataInfo.content =
      translatedDXAPIResponse.uiResources.context_data?.pyPortal;

    delete translatedDXAPIResponse.data.pyPortal;
    delete translatedDXAPIResponse.key;
  }

  if (nextAssignmentInfo) {
    translatedDXAPIResponse.data = translatedDXAPIResponse.data ?? {};
    translatedDXAPIResponse.data.nextAssignmentInfo = nextAssignmentInfo;
  }

  return translatedDXAPIResponse;
};

export default { getTranslatedDXAPIResponse };
